import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Check, Copy, Loader2, Monitor, ShieldCheck, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/utils';
import { Button } from '@/components/ui/button';
import { accountAuthApi } from '@/services/endpoints/account/auth';
import { evaluatePassword, isPasswordValid } from '@/utils/password-policy';
import { ACCOUNT_USER_KEYS } from '@/components/account/queryKeys';
import {
  accountInputClass,
  EmptyState,
  FieldLabel,
  LoadingRows,
  Notice,
  Panel,
  SectionTitle,
  StatusPill,
  formatDay,
} from '@/components/account/accountUi';

function apiMessage(error: unknown, fallback: string): string {
  const axiosError = error as AxiosError<{ message?: string; error?: string }>;
  return axiosError?.response?.data?.message || axiosError?.response?.data?.error || fallback;
}


function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copying failed. Select the codes and copy them manually.');
    }
  };

  return (
    <div className="space-y-4">
      <Notice tone="warn" title="Save these recovery codes now">
        Each code signs you in once if you lose your authenticator. They are shown only this time.
      </Notice>
      <ul className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted/30 p-4 font-mono text-sm tabular-nums sm:grid-cols-3">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => void copy()}>
          {copied ? (
            <Check className="h-4 w-4" aria-hidden="true" />
          ) : (
            <Copy className="h-4 w-4" aria-hidden="true" />
          )}
          {copied ? 'Copied' : 'Copy all'}
        </Button>
        <Button size="sm" onClick={onDone}>
          I have saved them
        </Button>
      </div>
    </div>
  );
}

export default function AccountSecurity() {
  const queryClient = useQueryClient();

  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [enrollment, setEnrollment] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [enrollCode, setEnrollCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [disableCode, setDisableCode] = useState('');
  const [disabling, setDisabling] = useState(false);

  const { data: mfa, isLoading: loadingMfa } = useQuery({
    queryKey: ACCOUNT_USER_KEYS.mfa,
    queryFn: accountAuthApi.getMfaStatus,
    staleTime: 60_000,
  });

  const { data: sessions, isLoading: loadingSessions } = useQuery({
    queryKey: ACCOUNT_USER_KEYS.sessions,
    queryFn: accountAuthApi.getSessions,
    staleTime: 60_000,
  });

  const changePassword = useMutation({
    mutationFn: () => accountAuthApi.changePassword(oldPassword, newPassword),
    onSuccess: () => {
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed.');
    },
    onError: (error) =>
      toast.error(apiMessage(error, 'Could not change your password. Check your current one.')),
  });

  const startEnroll = useMutation({
    mutationFn: accountAuthApi.enrollMfa,
    onSuccess: (data) => setEnrollment(data),
    onError: (error) => toast.error(apiMessage(error, 'Could not start two-factor setup.')),
  });

  const confirmEnroll = useMutation({
    mutationFn: () => accountAuthApi.confirmMfa(enrollCode.trim()),
    onSuccess: async (codes) => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_USER_KEYS.mfa });
      setEnrollment(null);
      setEnrollCode('');
      setRecoveryCodes(codes);
      toast.success('Two-factor authentication is on.');
    },
    onError: (error) =>
      toast.error(apiMessage(error, 'That code was not accepted. Try the current one.')),
  });

  const disableMfa = useMutation({
    mutationFn: () => accountAuthApi.disableMfa(disableCode.trim()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_USER_KEYS.mfa });
      setDisabling(false);
      setDisableCode('');
      toast.success('Two-factor authentication is off.');
    },
    onError: (error) => toast.error(apiMessage(error, 'That code was not accepted.')),
  });

  const regenerate = useMutation({
    mutationFn: () => accountAuthApi.regenerateRecoveryCodes(disableCode.trim()),
    onSuccess: async (codes) => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_USER_KEYS.mfa });
      setDisableCode('');
      setRecoveryCodes(codes);
      toast.success('New recovery codes generated.');
    },
    onError: (error) => toast.error(apiMessage(error, 'That code was not accepted.')),
  });

  const revoke = useMutation({
    mutationFn: (sessionName: string) => accountAuthApi.revokeSession(sessionName),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_USER_KEYS.sessions });
      toast.success('Session signed out.');
    },
    onError: (error) => toast.error(apiMessage(error, 'Could not end that session.')),
  });

  const checks = evaluatePassword(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmitPassword =
    oldPassword.length > 0 && isPasswordValid(newPassword) && passwordsMatch;

  const handlePasswordSubmit = (event: FormEvent) => {
    event.preventDefault();
    changePassword.mutate();
  };

  return (
    <>
      <SectionTitle
        title="Security"
        description="Your password, two-factor authentication, and where you are signed in."
      />

      <div className="space-y-6">
        <Panel title="Password">
          <form onSubmit={handlePasswordSubmit} className="max-w-md space-y-5">
            <div>
              <FieldLabel htmlFor="old-password">Current password</FieldLabel>
              <input
                id="old-password"
                type="password"
                className={accountInputClass}
                value={oldPassword}
                onChange={(event) => setOldPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <div>
              <FieldLabel htmlFor="new-password">New password</FieldLabel>
              <input
                id="new-password"
                type="password"
                className={accountInputClass}
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
              {newPassword.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {checks.map((check) => (
                    <li
                      key={check.id}
                      className={cn(
                        'flex items-center gap-1.5 text-xs',
                        check.met ? 'text-success' : 'text-muted-foreground'
                      )}
                    >
                      <Check
                        className={cn('h-3.5 w-3.5', check.met ? 'opacity-100' : 'opacity-30')}
                        aria-hidden="true"
                      />
                      {check.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <FieldLabel htmlFor="confirm-password">Confirm new password</FieldLabel>
              <input
                id="confirm-password"
                type="password"
                className={accountInputClass}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                autoComplete="new-password"
                required
              />
              {confirmPassword.length > 0 && !passwordsMatch && (
                <p className="mt-1.5 text-xs text-destructive">
                  The two passwords do not match.
                </p>
              )}
            </div>
            <Button type="submit" disabled={!canSubmitPassword || changePassword.isPending}>
              {changePassword.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : null}
              Change password
            </Button>
          </form>
        </Panel>

        <Panel
          title="Two-factor authentication"
          description="A 6-digit code from your authenticator app, required whenever you sign in from a new device."
          action={
            loadingMfa ? undefined : mfa?.enabled ? (
              <StatusPill tone="good">On</StatusPill>
            ) : (
              <StatusPill tone="warn">Off</StatusPill>
            )
          }
        >
          {loadingMfa ? (
            <LoadingRows rows={2} />
          ) : recoveryCodes ? (
            <RecoveryCodes codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />
          ) : enrollment ? (
            <div className="space-y-5">
              <div className="flex flex-wrap items-start gap-6">
                <div className="rounded-lg border border-border bg-white p-3">
                  <QRCodeSVG value={enrollment.otpauthUrl} size={148} level="M" />
                </div>
                <div className="min-w-[16rem] flex-1 space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Scan this with Google Authenticator, 1Password, Authy, or any TOTP app. Cannot
                    scan? Enter this key manually:
                  </p>
                  <code className="block break-all rounded-lg border border-border bg-muted/40 px-3 py-2 font-mono text-xs">
                    {enrollment.secret}
                  </code>
                  <div>
                    <FieldLabel htmlFor="enroll-code">Code from your app</FieldLabel>
                    <input
                      id="enroll-code"
                      className={`${accountInputClass} max-w-[12rem] text-center font-mono tracking-[0.3em]`}
                      value={enrollCode}
                      onChange={(event) => setEnrollCode(event.target.value)}
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                    />
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => confirmEnroll.mutate()}
                  disabled={enrollCode.trim().length < 6 || confirmEnroll.isPending}
                >
                  {confirmEnroll.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                  )}
                  Turn on two-factor
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setEnrollment(null);
                    setEnrollCode('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : mfa?.enabled ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Two-factor is on{mfa.enrolledAt ? ` since ${formatDay(mfa.enrolledAt)}` : ''}.{' '}
                <span className="font-mono tabular-nums">{mfa.recoveryCodesRemaining}</span>{' '}
                recovery {mfa.recoveryCodesRemaining === 1 ? 'code' : 'codes'} remaining.
              </p>

              <div className="max-w-sm">
                <FieldLabel htmlFor="mfa-code">Current authenticator code</FieldLabel>
                <input
                  id="mfa-code"
                  className={`${accountInputClass} max-w-[12rem] text-center font-mono tracking-[0.3em]`}
                  value={disableCode}
                  onChange={(event) => setDisableCode(event.target.value)}
                  inputMode="text"
                  maxLength={20}
                  placeholder="000000"
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Required to change either setting below.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => regenerate.mutate()}
                  disabled={!disableCode.trim() || regenerate.isPending}
                >
                  {regenerate.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  New recovery codes
                </Button>
                {disabling ? (
                  <>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => disableMfa.mutate()}
                      disabled={!disableCode.trim() || disableMfa.isPending}
                    >
                      {disableMfa.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                      ) : (
                        <ShieldOff className="h-4 w-4" aria-hidden="true" />
                      )}
                      Confirm turn off
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDisabling(false)}>
                      Keep it on
                    </Button>
                  </>
                ) : (
                  <Button variant="ghost" size="sm" onClick={() => setDisabling(true)}>
                    Turn off two-factor
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <p className="max-w-[65ch] text-sm text-muted-foreground">
                Without two-factor, a stolen password is enough to reach your billing details.
              </p>
              <Button onClick={() => startEnroll.mutate()} disabled={startEnroll.isPending}>
                {startEnroll.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                )}
                Set up two-factor
              </Button>
            </div>
          )}
        </Panel>

        <Panel
          title="Active sessions"
          description="Devices currently signed in to this account. Ending a session forces that device to sign in again."
          bodyClassName="p-0"
        >
          {loadingSessions ? (
            <div className="p-5">
              <LoadingRows rows={2} />
            </div>
          ) : sessions && sessions.length > 0 ? (
            <ul className="divide-y divide-border">
              {sessions.map((session) => (
                <li
                  key={`${session.realm}-${session.sessionName}`}
                  className="flex flex-wrap items-center justify-between gap-4 px-5 py-3"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/40">
                      <Monitor className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{session.sessionName}</p>
                      <p className="text-xs text-muted-foreground">
                        Last used {formatDay(session.lastUsedAt)} · started{' '}
                        {formatDay(session.createdAt)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => revoke.mutate(session.sessionName)}
                    disabled={revoke.isPending}
                  >
                    Sign out
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={Monitor}
              title="No other sessions"
              body="This is the only device signed in to your account."
            />
          )}
        </Panel>
      </div>
    </>
  );
}
